import React, { useState, useEffect, useCallback, useRef } from "react";
import styles from "./ReceivingBody.module.css";
import CompletedInvoiceModal from "../../pages/tables/ComponentsTable/CompletedInvoiceModal";
import { API_URL } from "../../config/api";
import axios from "axios";
import { FiTrash2, FiEdit } from "react-icons/fi";

/**
 * ✅ receivingItems — приходит из SalesMode
 * Формат: [{ id, name, ntin }]
 * onReceivingDraftUsed — колбэк, чтобы очистить draft в SalesMode после того, как мы его вставили
 */
const ReceivingBody = ({ warehouseId, receivingItems = [], onReceivingDraftUsed }) => {
  const token = localStorage.getItem("token");

  const [split, setSplit] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [items, setItems] = useState([]);
  const [stocks, setStocks] = useState({});

  const [modalOpen, setModalOpen] = useState(false);

  // чтобы не дублировать авто-добавление при перерендере
  const consumedIdsRef = useRef(new Set());

  // =========================================================================
  // 1) Остатки склада (stocks)
  // =========================================================================
  const loadStocks = useCallback(async () => {
    if (!warehouseId) return;

    try {
      const res = await axios.get(`${API_URL}/api/stocks?storage_id=${warehouseId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const stockMap = {};
      res.data?.data?.forEach((s) => {
        stockMap[s.product_id] = s.quantity;
      });

      setStocks(stockMap);
    } catch (err) {
      console.error("Ошибка при загрузке остатков:", err);
    }
  }, [warehouseId, token]);

  useEffect(() => {
    loadStocks();
  }, [loadStocks]);

  // =========================================================================
  // 2) Поиск товаров (suggestions)
  // =========================================================================
  useEffect(() => {
    if (searchTerm.length < 3) {
      setSuggestions([]);
      return;
    }

    const delayDebounce = setTimeout(async () => {
      try {
        const response = await axios.get(
          `${API_URL}/api/products?name_ru=${encodeURIComponent(searchTerm)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuggestions(response.data?.data || []);
        setShowSuggestions(true);
      } catch (error) {
        console.error("Ошибка при получении товаров:", error);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm, token]);

  // =========================================================================
  // 3) Вставка товара в таблицу (как из поиска, так и из SalesMode)
  // =========================================================================
  const buildItemFromProduct = useCallback(
    (product) => {
      const purchase_price = Number(product.purchase_price || 0);
      const sell_price = Number(product.sell_price || 0);
      const quantity = 1;

      const profit_percentage =
        purchase_price > 0 ? ((sell_price - purchase_price) / purchase_price) * 100 : 0;

      const id = product.id ?? product.product_id;

      return {
        id,
        name: product.name_ru ?? product.name ?? "",
        ntin: product.ntin || "",
        quantity,
        unit: "шт",
        purchase_price,
        sell_price,
        profit_percentage: Number(profit_percentage).toFixed(2),
        stockQuantity: stocks[id] || 0,
        totalQuantity: quantity,
        total: sell_price * quantity,
      };
    },
    [stocks]
  );

  const addItemToTable = useCallback(
    (product) => {
      const id = product?.id ?? product?.product_id;
      if (!id) return;

      setSearchTerm("");
      setShowSuggestions(false);

      setItems((prev) => {
        if (prev.some((i) => i.id === id)) return prev;
        const newItem = buildItemFromProduct({ ...product, id });
        return [...prev, newItem];
      });
    },
    [buildItemFromProduct]
  );

  const handleSelect = (product) => {
    // product из /api/products?name_ru=...
    addItemToTable(product);
  };

  // =========================================================================
  // 4) Авто-добавление товара(ов), пришедших из SalesMode (receivingItems)
  // =========================================================================
  const fetchProductById = useCallback(
    async (id) => {
      // Пытаемся максимально "угадать" endpoint.
      // 1) /api/products/{id}
      // 2) /api/products?id={id}
      // Если оба не работают — вернем null
      try {
        const r1 = await axios.get(`${API_URL}/api/products/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const p = r1.data?.data ?? r1.data;
        if (p) return p;
      } catch (_) {}

      try {
        const r2 = await axios.get(`${API_URL}/api/products?id=${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // иногда API возвращает массив
        const data = r2.data?.data;
        if (Array.isArray(data)) return data[0] || null;
        return data || null;
      } catch (e) {
        console.error("Не удалось получить продукт по ID:", id, e);
        return null;
      }
    },
    [token]
  );

  useEffect(() => {
    if (!warehouseId) return;
    if (!receivingItems || receivingItems.length === 0) return;

    const run = async () => {
      const toConsume = [];

      for (const it of receivingItems) {
        const id = it?.id;
        if (!id) continue;

        // чтобы не добавлять повторно один и тот же id
        if (consumedIdsRef.current.has(id)) continue;

        // если уже есть в таблице — тоже не добавляем
        if (items.some((x) => x.id === id)) {
          consumedIdsRef.current.add(id);
          continue;
        }

        // пробуем подтянуть полные данные товара
        const full = await fetchProductById(id);

        if (full) {
          addItemToTable({
            ...full,
            id,
            // если API вдруг не вернул name_ru — подстрахуемся
            name_ru: full.name_ru ?? it.name ?? full.name,
            ntin: full.ntin ?? it.ntin ?? "",
          });
        } else {
          // если endpoint неизвестен — добавим хотя бы "болванку" с названием/ntin
          addItemToTable({
            id,
            name_ru: it.name || "Товар",
            ntin: it.ntin || "",
            purchase_price: 0,
            sell_price: 0,
          });
        }

        consumedIdsRef.current.add(id);
        toConsume.push(id);
      }

      // После вставки — обновим остатки (чтобы stockQuantity был свежий)
      await loadStocks();

      // Скажем родителю очистить draft (чтобы при следующем открытии не вставлялось повторно)
      if (toConsume.length > 0 && typeof onReceivingDraftUsed === "function") {
        onReceivingDraftUsed();
      }
    };

    run();
    // важно: items тут в deps, чтобы проверка "уже есть" работала
  }, [
    warehouseId,
    receivingItems,
    items,
    addItemToTable,
    fetchProductById,
    loadStocks,
    onReceivingDraftUsed,
  ]);

  // Когда обновились stocks — обновим stockQuantity у уже добавленных позиций
  useEffect(() => {
    setItems((prev) =>
      prev.map((it) => ({
        ...it,
        stockQuantity: stocks[it.id] || 0,
      }))
    );
  }, [stocks]);

  // =========================================================================
  // 5) Изменение полей / удаление / очистка
  // =========================================================================
  const handleDelete = (id) => setItems((prev) => prev.filter((i) => i.id !== id));
  const clearCart = () => setItems([]);

  const handleFieldChange = (id, field, value) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          let updatedItem = { ...item };

          if (["quantity", "purchase_price", "sell_price", "profit_percentage"].includes(field)) {
            updatedItem[field] = Number(value) || 0;
          } else {
            updatedItem[field] = value;
          }

          if (field === "profit_percentage") {
            updatedItem.sell_price =
              updatedItem.purchase_price * (1 + updatedItem.profit_percentage / 100);
          }

          if (field === "sell_price") {
            updatedItem.profit_percentage =
              updatedItem.purchase_price > 0
                ? ((updatedItem.sell_price - updatedItem.purchase_price) / updatedItem.purchase_price) *
                  100
                : 0;
          }

          updatedItem.totalQuantity = updatedItem.quantity;
          updatedItem.total = updatedItem.sell_price * updatedItem.quantity;

          return updatedItem;
        }
        return item;
      })
    );
  };

  const total = items.reduce((sum, item) => sum + item.total, 0);
  const selectedItems = items;

  // =========================================================================
  // 6) Submit (создание приемки)
  // =========================================================================
  const handleSubmit = async ({ supplierId, paymentMethodId, shiftId, date }) => {
    if (!warehouseId || items.length === 0) {
      alert("Выберите склад и добавьте хотя бы один товар.");
      return;
    }

    const payloadItems = items.map((product) => ({
      product_id: product.id,
      quantity: parseFloat(product.quantity),
      purchase_price: parseFloat(product.purchase_price),
      sell_price: parseFloat(product.sell_price),
      amount: parseFloat(product.sell_price) * parseFloat(product.quantity),
      p_income: parseFloat(product.sell_price) * parseFloat(product.quantity),
      p_profit:
        (parseFloat(product.sell_price) - parseFloat(product.purchase_price)) *
        parseFloat(product.quantity),
    }));

    const payload = {
      storage_id: warehouseId,
      type: 1,
      quantity: payloadItems.reduce((sum, p) => sum + p.quantity, 0),
      status: "approved",
      amount: payloadItems.reduce((sum, p) => sum + p.amount, 0),
      p_income: payloadItems.reduce((sum, p) => sum + p.p_income, 0),
      p_profit: payloadItems.reduce((sum, p) => sum + p.p_profit, 0),
      wallet_id: paymentMethodId,
      items: payloadItems,
      supplier_id: supplierId,
      shift_id: shiftId,
      date,
    };

    try {
      await axios.post(`${API_URL}/api/stock_changes`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      clearCart();
      setModalOpen(false);
      alert("Приемка успешно создана!");

      await loadStocks();
    } catch (error) {
      console.error("Ошибка при отправке приемки:", error);
      alert("Ошибка при отправке приемки.");
    }
  };

  const handlePay = (data) => handleSubmit(data);
  const handlePayLater = (data) => handleSubmit(data);

  return (
    <div className={styles.body}>
      {/* 🔍 Поисковая строка */}
      <div className={styles.searchBar}>
        <input
          type="text"
          placeholder="Поиск товара..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        />

        <div className={styles.searchActions}>
          <button>⌨</button>
          <button onClick={() => setSplit(!split)}>▦</button>
        </div>

        {showSuggestions && suggestions.length > 0 && (
          <ul className={styles.suggestionList}>
            {suggestions.map((item) => (
              <li key={item.id} onClick={() => handleSelect(item)}>
                {item.name_ru}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* 📦 Список товаров */}
      <div className={`${styles.itemsWrapper} ${split ? styles.split : ""}`}>
        <div className={styles.itemsList}>
          {items.length === 0 ? (
            <div className={styles.empty}>
              <img src="/empty.png" alt="Пусто" />
              <p>Здесь пока пусто</p>
            </div>
          ) : (
            <table className={styles.productsTable}>
              <thead>
                <tr>
                  <th>Название</th>
                  <th>Штрихкод</th>
                  <th>Кол-во</th>
                  <th>Ед. изм.</th>
                  <th>Цена закупки</th>
                  <th>Накрутка %</th>
                  <th>Цена продажи</th>
                  <th>Остаток</th>
                  <th>Итого кол-во</th>
                  <th>Итого</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.name}</td>
                    <td>{item.ntin}</td>

                    <td>
                      <div className={styles.quantityWrapper}>
                        <button
                          onClick={() =>
                            handleFieldChange(item.id, "quantity", Math.max(0, item.quantity - 1))
                          }
                          className={styles.quantityBtn}
                        >
                          −
                        </button>
                        <span className={styles.quantityText}>{item.quantity}</span>
                        <button
                          onClick={() => handleFieldChange(item.id, "quantity", item.quantity + 1)}
                          className={styles.quantityBtn}
                        >
                          +
                        </button>
                      </div>
                    </td>

                    <td>{item.unit}</td>

                    <td>
                      <input
                        type="number"
                        step="0.01"
                        value={item.purchase_price}
                        onChange={(e) =>
                          handleFieldChange(item.id, "purchase_price", e.target.value)
                        }
                        style={{ width: "80px" }}
                      />
                      ₸
                    </td>

                    <td>
                      <input
                        type="number"
                        step="0.01"
                        value={item.profit_percentage}
                        onChange={(e) =>
                          handleFieldChange(item.id, "profit_percentage", e.target.value)
                        }
                        style={{ width: "60px" }}
                      />
                      %
                    </td>

                    <td>
                      <input
                        type="number"
                        step="0.01"
                        value={item.sell_price}
                        onChange={(e) => handleFieldChange(item.id, "sell_price", e.target.value)}
                        style={{ width: "80px" }}
                      />
                      ₸
                    </td>

                    <td>{item.stockQuantity}</td>
                    <td>{item.totalQuantity}</td>
                    <td>{item.total.toLocaleString()} ₸</td>

                    <td>
                      <button onClick={() => handleDelete(item.id)} style={{ color: "red" }}>
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.footerLeft}>
          <div className={styles.footerTop}>
            <span>Выбрано позиций:</span>
            <strong>{selectedItems.length}</strong>
          </div>

          <div className={styles.footerActions}>
            <button className={styles.footerBtn}>
              <FiEdit />
              <span>Редактировать</span>
            </button>

            <button className={`${styles.footerBtn} ${styles.danger}`} onClick={clearCart}>
              <FiTrash2 />
              <span>Удалить всё</span>
            </button>
          </div>
        </div>

        <div className={styles.footerRight}>
          <div className={styles.total}>
            <span>Итого:</span>
            <strong>{total.toLocaleString()} ₸</strong>
          </div>

          <button className={styles.pay} onClick={() => setModalOpen(true)}>
            CОЗДАТЬ ПРИЕМКУ
          </button>
        </div>
      </div>

      <CompletedInvoiceModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onPay={handlePay}
        onPayLater={handlePayLater}
        warehouseId={warehouseId}
      />
    </div>
  );
};

export default ReceivingBody;
